import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWallet } from '@txnlab/use-wallet';
import { motion } from 'framer-motion';
import { get } from 'lodash';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import 'react-tooltip/dist/react-tooltip.css';
import { XP_LEVELS } from '../config/kittyLevels';
import { updateKittyLikes } from '../firebase-client';
import { useAlgo } from '../hooks/useAlgo';
import { useKittiesStore } from '../hooks/useKittiesStore';
import formatNumber from '../utils/formatNumber';
import { getKittyName } from '../utils/kittyFormatting';
import { delistListing } from '../utils/marketplace/listing';
import Button from './Button';
import IconEdit from './IconEdit';
import KittyModalDetail from './KittyModalDetail';
import KittyModalEdit from './KittyModalEdit';
import { KittyProgressBar } from './KittyProgressBar';
import MarketplaceSellForm from './MarketplaceSellForm';
import Modal from './Modal';
import ModalContent from './ModalContent';
import NftImage from './NftImage';
import Tag from './Tag';

function KittyPreview({ kitty, rounded, gen, imageOnly, preventDefault = false, inMyListings = false }) {
  const router = useRouter();
  const { address } = useAlgo();
  const { signer } = useWallet();
  const sender = { signer, addr: address };
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('bio');
  const { currentGen } = useKittiesStore();
  const { address: addressFromUrl } = router.query;
  const isProfile = router.pathname === '/profile' && !addressFromUrl;
  const isMyProfile = (router.pathname === '/profile' && addressFromUrl === address) || (router.pathname === '/profile' && !addressFromUrl);
  const [isLiked, setIsLiked] = useState(kitty.bio?.likes?.includes(address) || false);
  const [numberOfLikes, setNumberOfLikes] = useState(kitty.bio?.likes?.length || 0);
  const theBio = kitty.bio ?? {};
  const queryClient = useQueryClient();

  const percentToLevelUp = Math.floor((get(theBio, 'currentExp', 0) / get(theBio, 'nextLevelExp', XP_LEVELS[1])) * 100);

  const likeMutation = useMutation({
    mutationFn: (data) => {
      if (!address) {
        toast.error('Please connect your wallet.');
        return;
      }
      return updateKittyLikes(data);
    },
    onError: (error) => {
      setIsLiked(!isLiked);
      setNumberOfLikes(isLiked ? numberOfLikes - 1 : numberOfLikes + 1);
      toast.error(error?.response?.data || 'Unexpected error');
    },
    onMutate: () => {
      setIsLiked(!isLiked);
      setNumberOfLikes(isLiked ? numberOfLikes - 1 : numberOfLikes + 1);
    },
  });

  const delistMutation = useMutation({
    mutationFn: () => {
      return delistListing(signer, address, sender, kitty.id);
    },
    onSuccess: () => {
      toast.success('Listing delisted');
    },
    onError: (error) => {
      toast.error(error?.response?.data || 'Unexpected error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['userListings', address] });
      queryClient.invalidateQueries({ queryKey: ['userAssets', address] });
    },
  });

  const onOpenFormModal = () => {
    if (preventDefault) return;
    setModalType('form');
    setModalOpen(true);
  };

  const onOpenBioModal = () => {
    if (preventDefault) return;
    setModalType('bio');
    setModalOpen(true);
  };

  const onClose = () => {
    setModalOpen(false);
  };

  const kittyName = getKittyName(kitty);

  if ((!currentGen && !gen) || !kitty) return <></>;

  useEffect(() => {
    if (modalOpen) return;

    if (kitty?.id === parseInt(router.query?.kitty)) {
      onOpenBioModal();
    }
  }, [router.query?.kitty]);

  return (
    <div className={`relative overflow-hidden group shadow-md bg-neutral-900 flex flex-col ${rounded ? 'rounded-lg' : ''}`}>
      <div className='relative z-10'>
        <div className='block text-[0px] aspect-square'>
          <button type='button' onClick={onOpenBioModal}>
            <NftImage nft={kitty} width={400} height={400} className='absolute inset-0 object-cover w-full h-full' />
          </button>
        </div>
      </div>

      {!imageOnly && (
        <div className=' flex-grow'>
          <div className='flex flex-col h-full'>
            <div className='flex-grow pb-4 px-4 pt-4'>
              <div className='flex justify-between'>
                <div className='flex-col'>
                  <div className='flex items-center gap-3'>
                    <h3 className='text-xl font-bold flex flex-col text-neutral-50'>
                      <span>{kittyName}</span>
                    </h3>
                    {address && isProfile && (
                      <button
                        theme='none'
                        className='text-white p-2 bg-gray-700 rounded-full transition duration-200 hover:bg-gray-800'
                        onClick={onOpenFormModal}
                      >
                        <IconEdit className='w-3.5 h-3.5' />
                      </button>
                    )}
                  </div>

                  <div>
                    <a
                      href={`https://www.downbad.farm/asset/${kitty.id}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-base text-neutral-400 font-normal'
                    >
                      <span>{kitty.id}</span>
                    </a>
                  </div>
                </div>

                <div>
                  <div className='flex items-center gap-3'>
                    <span className='text-lg font-medium'>{numberOfLikes}</span>
                    <motion.button
                      type='button'
                      whileTap={{ scale: 3 }}
                      transition={{ duration: 0.5 }}
                      onClick={() => likeMutation.mutate({ kittyId: kitty.id, address })}
                      className='cursor-pointer'
                    >
                      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' width='20' height='20'>
                        {!isLiked && (
                          // https://primer.style/octicons-v2/heart-16
                          <g className='text-white'>
                            <path
                              fill={'currentColor'}
                              fillRule='evenodd'
                              clipRule='evenodd'
                              d='M4.25 2.5C2.91421 2.5 1.5 3.66421 1.5 5.5C1.5 7.64969 3.08041 9.64375 4.8647 11.1819C5.73533 11.9325 6.60962 12.5358 7.26796 12.952C7.56407 13.1392 7.81492 13.2875 8 13.3934C8.18508 13.2875 8.43593 13.1392 8.73204 12.952C9.39039 12.5358 10.2647 11.9325 11.1353 11.1819C12.9196 9.64375 14.5 7.64969 14.5 5.5C14.5 3.66421 13.0858 2.5 11.75 2.5C10.3768 2.5 9.14121 3.48581 8.72114 4.95604C8.62915 5.27802 8.33486 5.5 8 5.5C7.66514 5.5 7.37085 5.27802 7.27886 4.95604C6.85879 3.48581 5.62319 2.5 4.25 2.5ZM8 14.25C7.65543 14.9162 7.65523 14.9161 7.655 14.9159L7.65269 14.9147L7.64731 14.9119L7.62883 14.9022C7.61313 14.8939 7.59074 14.882 7.5621 14.8665C7.50484 14.8356 7.42254 14.7904 7.3188 14.7317C7.1114 14.6142 6.81771 14.442 6.46642 14.2199C5.76538 13.7767 4.82717 13.13 3.8853 12.3181C2.04459 10.7312 0 8.35031 0 5.5C0 2.83579 2.08579 1 4.25 1C5.79736 1 7.15289 1.80151 8 3.01995C8.84711 1.80151 10.2026 1 11.75 1C13.9142 1 16 2.83579 16 5.5C16 8.35031 13.9554 10.7312 12.1147 12.3181C11.1728 13.13 10.2346 13.7767 9.53358 14.2199C9.18229 14.442 8.8886 14.6142 8.6812 14.7317C8.57746 14.7904 8.49517 14.8356 8.4379 14.8665C8.40926 14.882 8.38687 14.8939 8.37117 14.9022L8.35269 14.9119L8.34731 14.9147L8.34501 14.9159C8.34477 14.9161 8.34457 14.9162 8 14.25ZM8 14.25L8.34501 14.9159C8.12889 15.0277 7.87111 15.0277 7.655 14.9159L8 14.25Z'
                            />
                          </g>
                        )}
                        {isLiked && (
                          <g className='text-red-500'>
                            <path
                              fill={'currentColor'}
                              fillRule='evenodd'
                              clipRule='evenodd'
                              d='M7.655 14.9159C7.65523 14.9161 7.65543 14.9162 8 14.25C8.34457 14.9162 8.34477 14.9161 8.34501 14.9159C8.12889 15.0277 7.87111 15.0277 7.655 14.9159ZM7.655 14.9159L8 14.25L8.34501 14.9159L8.34731 14.9147L8.35269 14.9119L8.37117 14.9022C8.38687 14.8939 8.40926 14.882 8.4379 14.8665C8.49516 14.8356 8.57746 14.7904 8.6812 14.7317C8.8886 14.6142 9.18229 14.442 9.53358 14.2199C10.2346 13.7767 11.1728 13.13 12.1147 12.3181C13.9554 10.7312 16 8.35031 16 5.5C16 2.83579 13.9142 1 11.75 1C10.2026 1 8.84711 1.80151 8 3.01995C7.15289 1.80151 5.79736 1 4.25 1C2.08579 1 0 2.83579 0 5.5C0 8.35031 2.04459 10.7312 3.8853 12.3181C4.82717 13.13 5.76538 13.7767 6.46642 14.2199C6.81771 14.442 7.1114 14.6142 7.3188 14.7317C7.42254 14.7904 7.50484 14.8356 7.5621 14.8665C7.59074 14.882 7.61313 14.8939 7.62883 14.9022L7.64731 14.9119L7.65269 14.9147L7.655 14.9159Z'
                            />
                          </g>
                        )}
                      </svg>
                    </motion.button>
                  </div>
                </div>
              </div>

              <div className='flex flex-col gap-2 text-neutral-400 mt-3'>
                <div className='flex items-center gap-2'>
                  {kitty.rank && (
                    <Tag
                      heading='Rank'
                      label={`${formatNumber(kitty.rank)}/${
                        gen === 3 || kitty.gen === 3
                          ? '1,069'
                          : gen === 1 || kitty.gen === 1
                          ? 470
                          : gen === 2 || kitty.gen === 2
                          ? 500
                          : gen === 4 || kitty.gen === 4
                          ? '2,405'
                          : 0
                      }`}
                    />
                  )}

                  {typeof theBio?.stats?.finishedAdventures === 'number' && (
                    <Tag heading='Adventures' label={`${theBio?.stats?.finishedAdventures}`} />
                  )}

                  {typeof theBio?.stats?.finishedJobs === 'number' && <Tag heading='Jobs' label={`${theBio?.stats?.finishedJobs}`} />}

                  {typeof theBio?.stats?.wonFights === 'number' && <Tag heading='Fight Record' label={`${theBio?.stats?.wonFights || 0} wins`} />}
                </div>
              </div>

              {typeof percentToLevelUp === 'number' && <KittyProgressBar percentToLevelUp={percentToLevelUp} theBio={theBio} />}
            </div>
          </div>
        </div>
      )}

      <Modal open={modalOpen} onClose={onClose} type='large'>
        <ModalContent>
          {modalType === 'form' ? <KittyModalEdit kitty={kitty} onSuccess={onClose} /> : <KittyModalDetail kitty={kitty} onClose={onClose} />}
        </ModalContent>
      </Modal>

      {isMyProfile && !inMyListings && <MarketplaceSellForm kitty={kitty} />}

      {isMyProfile && inMyListings && signer && address && sender && (kitty?.id || kitty?.index) && (
        <div className='mb-4 flex justify-center w-full px-4 flex-col gap-2'>
          <p className='text-lg text-neutral-400 text-center mb-1'>On sale for {formatNumber(kitty.price / 1e6)} ALGO</p>
          <Button className='w-full' theme='outline' target='_blank' href={`https://www.downbad.farm/asset/${kitty.id || kitty.index}`}>
            Delist
          </Button>
        </div>
      )}
    </div>
  );
}

export default KittyPreview;
